import * as fmt from '../../lib/format';

// Shapes returned by /compliance/overview and /me/audit/*, plus the words used for each standard.

export type StandardId = 'naming' | 'flow' | 'timelog' | 'planning';

export interface StandardInfo {
  id: StandardId;
  label: string;
  /** What the percentage is, in a few words. */
  measure: string;
  /** The page that shows the details, for an admin. */
  page: string;
}

export const STANDARDS: StandardInfo[] = [
  { id: 'naming', label: 'Naming', measure: 'tickets with a clear title', page: '/hygiene' },
  { id: 'flow', label: 'Flow', measure: 'moves by the usual owner', page: '/flow' },
  { id: 'timelog', label: 'Time logging', measure: 'entries with one standard tag', page: '/effort' },
  { id: 'planning', label: 'Planning', measure: 'goal tickets shipped or ready', page: '/planning' },
];

export interface Standing {
  pct: number | null;
  /** Things judged: tickets, moves, entries, goal tickets. */
  total: number;
  /** Of those, the ones that need a look. */
  flagged: number;
  previous_pct: number | null;
  /** Points against the month before. */
  change: number | null;
}

export interface Checkpoint {
  reached: boolean;
  meets_target: boolean | null;
  target_pct: number;
}

export interface Standings {
  naming: Standing | null;
  flow: (Standing & { rework: number }) | null;
  timelog: Standing | null;
  planning: (Standing & { epics: number; checkpoint: Checkpoint | null }) | null;
}

export interface OverviewProject extends Standings {
  project_key: string;
  project_name: string | null;
  sentence: string;
}

export interface Overview {
  period: string;
  previous_period: string;
  planning_quarter: { id: string; label: string } | null;
  overall: Standings & { sentence: string };
  projects: OverviewProject[];
}

/** The words under a percentage, or why there is none. */
export function standingCaption(id: StandardId, s: Standing | null, planningQuarter?: string | null): string {
  const n = (v: number) => fmt.count(v);
  if (id === 'timelog' && s === null) return 'Not judged before the tagging standard started';
  if (id === 'planning' && s === null) return 'No goal epics in this quarter';
  if (s === null) return id === 'flow' ? 'No moves this month' : id === 'naming' ? 'No tickets created this month' : 'No entries this month';
  if (s.pct === null) return id === 'flow' ? 'No move could be judged yet' : 'Nothing to judge this month';
  switch (id) {
    case 'naming':
      return s.flagged === 0 ? `All ${n(s.total)} ${s.total === 1 ? 'title is' : 'titles are'} clear` : `${n(s.flagged)} of ${n(s.total)} ${s.flagged === 1 ? 'title needs' : 'titles need'} attention`;
    case 'flow':
      return s.flagged === 0 ? `All ${n(s.total)} ${s.total === 1 ? 'move' : 'moves'} by the usual owner` : `${n(s.flagged)} of ${n(s.total)} ${s.total === 1 ? 'move' : 'moves'} outside the usual owner`;
    case 'timelog':
      return s.flagged === 0 ? `All ${n(s.total)} ${s.total === 1 ? 'entry has' : 'entries have'} one standard tag` : `${n(s.flagged)} of ${n(s.total)} ${s.flagged === 1 ? 'entry needs' : 'entries need'} a fix`;
    default:
      return `${n(s.total - s.flagged)} of ${n(s.total)} goal tickets shipped or ready${planningQuarter ? ` (${planningQuarter})` : ''}`;
  }
}
